import { useState, useCallback } from "react";
import { useNavigate } from "react-router";
import { apiFetch } from "@/lib/api-client";

const BUSINESS_TYPES = [
  { value: "dental", label: "Dental", icon: "🦷" },
  { value: "spa", label: "Spa & Wellness", icon: "🧖" },
  { value: "fitness", label: "Fitness", icon: "💪" },
  { value: "salon", label: "Salon", icon: "💇" },
  { value: "legal", label: "Legal", icon: "⚖️" },
  { value: "real_estate", label: "Real Estate", icon: "🏠" },
  { value: "plumbing", label: "Plumbing", icon: "🔩" },
  { value: "hvac", label: "HVAC", icon: "❄️" },
  { value: "landscaping", label: "Landscaping", icon: "🌿" },
  { value: "restaurant", label: "Restaurant", icon: "🍽" },
  { value: "accounting", label: "Accounting", icon: "📊" },
  { value: "pet_grooming", label: "Pet Grooming", icon: "🐾" },
  { value: "tutoring", label: "Tutoring", icon: "📚" },
] as const;

type BusinessType = (typeof BUSINESS_TYPES)[number]["value"];

const SERVICE_DEFAULTS: Record<string, string[]> = {
  dental: ["Cleaning", "Whitening", "Filling", "Crown", "Root Canal", "Checkup"],
  spa: ["Swedish Massage", "Deep Tissue", "Facial", "Body Wrap", "Hot Stone", "Aromatherapy"],
  real_estate: ["Buyer Consultation", "Listing Presentation", "Home Valuation", "Open House"],
  fitness: ["Personal Training", "Group Class", "Nutrition Plan", "Assessment", "Boot Camp"],
  restaurant: ["Dine-In", "Catering", "Private Event", "Tasting Menu"],
  legal: ["Consultation", "Contract Review", "Estate Planning", "Business Formation"],
  accounting: ["Tax Prep (Individual)", "Tax Prep (Business)", "Bookkeeping", "Payroll", "Audit"],
  plumbing: ["Drain Cleaning", "Pipe Repair", "Water Heater", "Fixture Install", "Inspection"],
  hvac: ["AC Tune-Up", "Furnace Repair", "Duct Cleaning", "System Install", "Emergency Call"],
  landscaping: ["Lawn Mowing", "Garden Design", "Tree Trimming", "Irrigation", "Hardscaping"],
  salon: ["Haircut", "Color", "Blowout", "Highlights", "Keratin Treatment", "Extensions"],
  pet_grooming: ["Bath & Brush", "Full Groom", "Nail Trim", "De-Shedding", "Teeth Cleaning"],
  tutoring: ["1-on-1 Session", "Group Session", "Test Prep", "Homework Help", "Assessment"],
};

const PRICE_DEFAULTS: Record<string, [number, number]> = {
  dental: [50, 500], spa: [60, 200], fitness: [30, 150], salon: [25, 300],
  legal: [100, 500], real_estate: [0, 500], plumbing: [50, 500], hvac: [75, 600],
  landscaping: [40, 300], restaurant: [25, 200], accounting: [75, 400],
  pet_grooming: [30, 120], tutoring: [30, 100],
};

const COLOR_PRESETS = [
  { name: "Ocean", primary: "#0EA5E9", accent: "#38BDF8" },
  { name: "Forest", primary: "#16A34A", accent: "#4ADE80" },
  { name: "Sunset", primary: "#F97316", accent: "#FB923C" },
  { name: "Royal", primary: "#7C3AED", accent: "#A78BFA" },
  { name: "Rose", primary: "#E11D48", accent: "#FB7185" },
  { name: "Slate", primary: "#475569", accent: "#94A3B8" },
] as const;

const TOTAL_STEPS = 5;

interface SetupData {
  businessType: BusinessType | null;
  selectedServices: string[];
  servicePrices: Record<string, { min: number; max: number }>;
  businessName: string;
  logoFile: File | null;
  colorPreset: string | null;
}

export default function GuidedSetupPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [customService, setCustomService] = useState("");
  const [data, setData] = useState<SetupData>({
    businessType: null,
    selectedServices: [],
    servicePrices: {},
    businessName: "",
    logoFile: null,
    colorPreset: null,
  });

  const canProceed = (): boolean => {
    if (step === 1) return data.businessType !== null;
    if (step === 2) return data.selectedServices.length > 0;
    if (step === 3) return true;
    if (step === 4) return data.businessName.trim().length > 0;
    return true;
  };

  const handleSelectType = (type: BusinessType) => {
    const defaults = SERVICE_DEFAULTS[type] ?? [];
    const priceRange = PRICE_DEFAULTS[type] ?? [20, 200];
    const prices: Record<string, { min: number; max: number }> = {};
    for (const svc of defaults) {
      prices[svc] = { min: priceRange[0], max: priceRange[1] };
    }
    setData({ ...data, businessType: type, selectedServices: defaults, servicePrices: prices });
  };

  const handleToggleService = (service: string) => {
    const isSelected = data.selectedServices.includes(service);
    const updated = isSelected
      ? data.selectedServices.filter((s) => s !== service)
      : [...data.selectedServices, service];
    const prices = { ...data.servicePrices };
    if (!isSelected && !prices[service]) {
      const range = PRICE_DEFAULTS[data.businessType ?? ""] ?? [20, 200];
      prices[service] = { min: range[0], max: range[1] };
    }
    setData({ ...data, selectedServices: updated, servicePrices: prices });
  };

  const handleAddCustom = () => {
    const trimmed = customService.trim();
    if (trimmed && !data.selectedServices.includes(trimmed)) {
      handleToggleService(trimmed);
      setCustomService("");
    }
  };

  const handlePriceChange = (service: string, field: "min" | "max", value: number) => {
    const current = data.servicePrices[service] ?? { min: 0, max: 200 };
    const nextPrice =
      field === "min"
        ? { min: value, max: Math.max(value, current.max) }
        : { min: Math.min(value, current.min), max: value };

    setData({
      ...data,
      servicePrices: {
        ...data.servicePrices,
        [service]: nextPrice,
      },
    });
  };

  const handleLogoDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const file = e.dataTransfer.files[0];
      if (file?.type.startsWith("image/")) {
        setData((prev) => ({ ...prev, logoFile: file }));
      }
    },
    [],
  );

  const handleLogoClick = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file?.type.startsWith("image/")) {
        setData((prev) => ({ ...prev, logoFile: file }));
      }
    };
    input.click();
  }, []);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await apiFetch("/api/onboarding/guided-setup/complete", {
        method: "POST",
        body: JSON.stringify({
          businessType: data.businessType,
          services: data.selectedServices.map((name) => ({
            name,
            priceMin: data.servicePrices[name]?.min ?? 0,
            priceMax: data.servicePrices[name]?.max ?? 0,
          })),
          businessName: data.businessName,
          colorPreset: data.colorPreset,
          hasLogo: data.logoFile !== null,
        }),
      });
      navigate("/onboarding/brand-voice", { replace: true });
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Couldn't complete guided setup. Please try again.");
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* Progress bar */}
      <div className="flex gap-2 px-6 pt-6">
        {Array.from({ length: TOTAL_STEPS }, (_, i) => (
          <div
            key={i}
            className={`h-1.5 flex-1 rounded-full transition-colors ${
              i < step ? "bg-primary" : "bg-border"
            }`}
          />
        ))}
      </div>
      <p className="px-6 pt-2 text-xs text-muted-foreground">Step {step} of {TOTAL_STEPS}</p>

      <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col px-6 py-8">
        {/* Step 1: Business Type */}
        {step === 1 && (
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground">What type of business do you run?</h1>
              <p className="mt-1 text-muted-foreground">We will customize everything for your industry.</p>
            </div>
            <div className="grid grid-cols-4 gap-3 sm:grid-cols-5">
              {BUSINESS_TYPES.map((bt) => (
                <button
                  key={bt.value}
                  onClick={() => handleSelectType(bt.value)}
                  className={`flex flex-col items-center gap-1.5 rounded-lg border p-3 text-center transition-colors ${
                    data.businessType === bt.value
                      ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                      : "border-border hover:border-primary/50"
                  }`}
                >
                  <span className="text-2xl">{bt.icon}</span>
                  <span className="text-xs font-medium text-foreground">{bt.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Services */}
        {step === 2 && (
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Select your services</h1>
              <p className="mt-1 text-muted-foreground">Tap to toggle. Add custom services below.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {(SERVICE_DEFAULTS[data.businessType ?? ""] ?? []).map((svc) => (
                <button
                  key={svc}
                  onClick={() => handleToggleService(svc)}
                  className={`rounded-full border px-4 py-2 text-sm transition-colors ${
                    data.selectedServices.includes(svc)
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border text-muted-foreground hover:border-primary/50"
                  }`}
                >
                  {svc}
                </button>
              ))}
              {data.selectedServices
                .filter((s) => !(SERVICE_DEFAULTS[data.businessType ?? ""] ?? []).includes(s))
                .map((svc) => (
                  <button
                    key={svc}
                    onClick={() => handleToggleService(svc)}
                    className="rounded-full border border-primary bg-primary px-4 py-2 text-sm text-primary-foreground"
                  >
                    {svc}
                  </button>
                ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={customService}
                onChange={(e) => setCustomService(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddCustom()}
                placeholder="Add custom service..."
                className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground"
              />
              <button
                onClick={handleAddCustom}
                className="rounded-md bg-secondary px-4 py-2 text-sm font-medium text-secondary-foreground hover:bg-secondary/80"
              >
                Add
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Pricing */}
        {step === 3 && (
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Set your price ranges</h1>
              <p className="mt-1 text-muted-foreground">Drag the sliders to adjust min/max for each service.</p>
            </div>
            <div className="max-h-[50vh] space-y-4 overflow-y-auto pr-2">
              {data.selectedServices.map((svc) => {
                const price = data.servicePrices[svc] ?? { min: 0, max: 200 };
                return (
                  <div key={svc} className="rounded-lg border border-border p-4">
                    <p className="mb-2 text-sm font-medium text-foreground">{svc}</p>
                    <div className="grid grid-cols-2 gap-4">
                      <label className="space-y-1">
                        <span className="text-xs text-muted-foreground">Min: ${price.min}</span>
                        <input
                          type="range"
                          min={0}
                          max={2000}
                          step={5}
                          value={price.min}
                          onChange={(e) => handlePriceChange(svc, "min", Number(e.target.value))}
                          className="w-full accent-primary"
                        />
                      </label>
                      <label className="space-y-1">
                        <span className="text-xs text-muted-foreground">Max: ${price.max}</span>
                        <input
                          type="range"
                          min={0}
                          max={5000}
                          step={5}
                          value={price.max}
                          onChange={(e) => handlePriceChange(svc, "max", Number(e.target.value))}
                          className="w-full accent-primary"
                        />
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 4: Brand */}
        {step === 4 && (
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Brand your account</h1>
              <p className="mt-1 text-muted-foreground">Upload a logo or pick a color scheme.</p>
            </div>
            <input
              type="text"
              value={data.businessName}
              onChange={(e) => setData({ ...data, businessName: e.target.value })}
              placeholder="Business name"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground placeholder:text-muted-foreground"
            />
            <div
              onClick={handleLogoClick}
              onDrop={handleLogoDrop}
              onDragOver={(e) => e.preventDefault()}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleLogoClick();
                }
              }}
              role="button"
              aria-label="Upload logo - Drag and drop or click to browse"
              aria-describedby="logo-upload-help"
              className="flex h-32 items-center justify-center rounded-lg border-2 border-dashed border-border text-sm text-muted-foreground hover:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              {data.logoFile ? (
                <span className="font-medium text-foreground">{data.logoFile.name}</span>
              ) : (
                <span>Drag & drop your logo here or click to browse files</span>
              )}
            </div>
            <p id="logo-upload-help" className="text-xs text-muted-foreground">
              Supported formats: PNG, JPG. Maximum file size: 5MB
            </p>
            <div className="grid grid-cols-3 gap-3">
              {COLOR_PRESETS.map((preset) => (
                <button
                  key={preset.name}
                  onClick={() => setData({ ...data, colorPreset: preset.name })}
                  className={`flex items-center gap-3 rounded-lg border p-3 transition-colors ${
                    data.colorPreset === preset.name
                      ? "border-primary ring-2 ring-primary/20"
                      : "border-border hover:border-primary/50"
                  }`}
                >
                  <div className="flex gap-1">
                    <div className="h-6 w-6 rounded-full" style={{ backgroundColor: preset.primary }} />
                    <div className="h-6 w-6 rounded-full" style={{ backgroundColor: preset.accent }} />
                  </div>
                  <span className="text-sm font-medium text-foreground">{preset.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 5: Confirm */}
        {step === 5 && (
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Confirm your setup</h1>
              <p className="mt-1 text-muted-foreground">Review everything before we configure your account.</p>
            </div>
            <div className="space-y-4 rounded-lg border border-border p-4">
              <SummaryRow label="Business" value={data.businessName || "Not set"} />
              <SummaryRow
                label="Industry"
                value={BUSINESS_TYPES.find((b) => b.value === data.businessType)?.label ?? "None"}
              />
              <SummaryRow label="Services" value={`${data.selectedServices.length} selected`} />
              <SummaryRow label="Color" value={data.colorPreset ?? "Default"} />
              <SummaryRow label="Logo" value={data.logoFile ? data.logoFile.name : "None"} />
            </div>
            <ul className="max-h-40 space-y-1 overflow-y-auto text-sm text-muted-foreground">
              {data.selectedServices.map((svc) => {
                const price = data.servicePrices[svc];
                return (
                  <li key={svc} className="flex justify-between">
                    <span>{svc}</span>
                    <span>${price?.min ?? 0} - ${price?.max ?? 0}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {submitError && (
          <div
            role="alert"
            aria-live="assertive"
            className="mt-6 rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive"
          >
            {submitError}
          </div>
        )}

        {/* Navigation */}
        <div className="mt-auto flex items-center justify-between pt-8">
          {step > 1 ? (
            <button
              onClick={() => setStep(step - 1)}
              className="text-sm font-medium text-muted-foreground hover:text-foreground"
            >
              Back
            </button>
          ) : (
            <div />
          )}
          {step < TOTAL_STEPS ? (
            <button
              onClick={() => canProceed() && setStep(step + 1)}
              disabled={!canProceed()}
              className="rounded-md bg-primary px-6 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              Continue
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="rounded-md bg-primary px-6 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {isSubmitting ? "Setting up..." : "Set Up My Account"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-foreground">{value}</span>
    </div>
  );
}
